/**
 * approvalStore.js
 * ─────────────────
 * Pending change queue for the two-person rule. When an admin proposes a
 * sensitive change (status transition on a joint assessment, closing a
 * case, publishing a pay band), the change is recorded as a PendingChange
 * and is NOT applied. An approver must approve it before the underlying
 * record is mutated.
 *
 * PendingChange shape
 * ───────────────────
 *   id             , unique identifier
 *   action         , e.g. 'assessment.changeStatus', 'assessment.close'
 *   targetType     , 'assessment' | 'payBand' | 'user' | etc.
 *   targetId       , id of the object being changed
 *   proposedBy     , user id of the proposer
 *   proposedAt     , ISO timestamp
 *   patch          , { fieldName: newValue, ... } to apply on approval
 *   reason         , admin's stated reason
 *   status         , 'pending' | 'approved' | 'rejected' | 'cancelled'
 *   reviewedBy     , approver who acted on it
 *   reviewedAt     , ISO timestamp of approval/rejection
 *   reviewNote     , approver's comment (especially for rejections)
 *
 * Self-approval prevention
 * ─────────────────────────
 * The approve() function rejects with an error if the approver's id
 * matches proposedBy. Even if both users have the approver role, a single
 * person cannot be both proposer and approver of the same change.
 */

import { getApproverRole } from './tenantConfigStore';

const STORAGE_KEY = 'clearequal.approvals.v1';
const EVENT_NAME = 'clearequal.approvals.changed';

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    /* */
  }
}

/**
 * List all pending changes, optionally filtered by status.
 */
export function listPendingChanges(status = null) {
  const all = readAll();
  if (!status) return all;
  return all.filter((c) => c.status === status);
}

/**
 * Returns the list of pending changes that need approver action.
 */
export function getApprovalQueue() {
  return listPendingChanges('pending');
}

/**
 * Propose a sensitive change. Returns the created PendingChange.
 */
export function proposeChange({
  action,
  targetType,
  targetId,
  patch,
  proposedBy,
  reason = '',
}) {
  const all = readAll();
  const change = {
    id: `pc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    action,
    targetType,
    targetId,
    patch,
    proposedBy,
    proposedAt: new Date().toISOString(),
    reason,
    status: 'pending',
  };
  all.push(change);
  writeAll(all);
  return change;
}

/**
 * Approve a pending change. Throws if:
 *   - the change does not exist
 *   - the change is not in pending status
 *   - the approver is the same person who proposed it (self-approval blocked)
 *
 * The caller is responsible for invoking the actual mutation (e.g.
 * updating the assessment record), this store just records the approval.
 * The pattern is: call approve() → if it succeeds, apply the patch.
 */
export function approve(changeId, approverId, reviewNote = '') {
  const all = readAll();
  const idx = all.findIndex((c) => c.id === changeId);
  if (idx === -1) throw new Error('Pending change not found');

  const c = all[idx];
  if (c.status !== 'pending') {
    throw new Error(`Cannot approve, current status is ${c.status}`);
  }
  if (c.proposedBy === approverId) {
    throw new Error('Self-approval is not permitted (two-person rule)');
  }

  const updated = {
    ...c,
    status: 'approved',
    reviewedBy: approverId,
    reviewedAt: new Date().toISOString(),
    reviewNote,
  };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/**
 * Reject a pending change. Same self-check as approve.
 */
export function reject(changeId, approverId, reviewNote = '') {
  const all = readAll();
  const idx = all.findIndex((c) => c.id === changeId);
  if (idx === -1) throw new Error('Pending change not found');

  const c = all[idx];
  if (c.status !== 'pending') {
    throw new Error(`Cannot reject, current status is ${c.status}`);
  }
  if (c.proposedBy === approverId) {
    throw new Error('Cannot review your own proposal');
  }

  const updated = {
    ...c,
    status: 'rejected',
    reviewedBy: approverId,
    reviewedAt: new Date().toISOString(),
    reviewNote,
  };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/**
 * The proposer can cancel their own pending change before review.
 */
export function cancel(changeId, proposerId) {
  const all = readAll();
  const idx = all.findIndex((c) => c.id === changeId);
  if (idx === -1) throw new Error('Pending change not found');

  const c = all[idx];
  if (c.status !== 'pending') return c;
  if (c.proposedBy !== proposerId) {
    throw new Error('Only the proposer can cancel a pending change');
  }

  const updated = { ...c, status: 'cancelled', reviewedAt: new Date().toISOString() };
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/**
 * Gate an employee CV upload. The parsed CV is NOT applied to the profile
 * until an approver (works council rep or manager, per tenant) approves it.
 * Returns the created PendingChange.
 */
export function proposeCvUpload({ tenantId, employeeId, employeeName, proposedBy, parsedCv }) {
  return proposeChange({
    action: 'profile.cvUpload',
    targetType: 'employeeProfile',
    targetId: employeeId,
    patch: { parsedCv },
    proposedBy,
    reason: `CV upload from ${employeeName || 'employee'} awaiting verification before it counts toward skill match. Approver: ${getApproverRole(tenantId)}.`,
  });
}

/**
 * Gate an uncertain cross-source identity merge. The two records are NOT
 * merged until an approver confirms they are the same person.
 * Returns the created PendingChange.
 */
export function proposeIdentityMerge({ tenantId, pair, score, signals, proposedBy }) {
  const a = pair.a, b = pair.b;
  return proposeChange({
    action: 'identity.merge',
    targetType: 'employeeIdentity',
    targetId: `${a.source}:${a.sourceId}+${b.source}:${b.sourceId}`,
    patch: { records: [a, b], score, signals },
    proposedBy,
    reason: `Possible same person: ${a.firstName || ''} ${a.lastName || ''} (${a.source}) and ${b.firstName || ''} ${b.lastName || ''} (${b.source}) - ${Math.round(score * 100)}% match. Approver: ${getApproverRole(tenantId)}.`,
  });
}

export function subscribe(callback) {
  const handler = () => callback(listPendingChanges());
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener('storage', handler);
  };
}
